/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";

export const CheckAuthParams = z.object({
  selected_api: z
    .string()
    .describe(
      "REQUIRED: The API identifier to check authentication for (e.g., 'GoogleCalendarCLIAPI@1.5.0' or 'gmail'). This is a required parameter for the tool.",
    ),
  owner: z
    .string()
    .optional()
    .describe(
      "OPTIONAL: The user ID to check authentications for. If not provided, defaults to 'me' (current user). Pass a custom user_id to check authentications for a specific user.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Checking authentication...' or 'Verifying connection...'",
    ),
});

export const authenticationCheckExecute = async (
  rawArgs: z.infer<typeof CheckAuthParams>,
) => {
  let params: z.infer<typeof CheckAuthParams>;
  try {
    params = CheckAuthParams.parse(rawArgs || {});
  } catch (error) {
    console.error(
      "[AUTH_CHECK_TOOL] Error parsing arguments for authenticationCheck:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for authentication_check. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const { selected_api, owner } = params;

  const query = new URLSearchParams({
    selected_api,
  });

  // Add owner parameter if provided
  if (owner) {
    query.set("owner", owner);
  }
  const proxyApiUrl = `/api/canvasui/composer/authenticationCheck?${query.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for authentication_check (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for authentication_check (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for authentication_check (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for authentication_check (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[AUTH_CHECK_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[AUTH_CHECK_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error(
      "[AUTH_CHECK_TOOL] Error in authenticationCheckExecute:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error checking authentication: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const authenticationCheckDescription = `Checks if the user is authenticated with a specific app (selected_api) for a given account. 
Prefer using authentications that the user has permission to use.

Query Parameters:
- selected_api (REQUIRED): The API identifier for the app (e.g., 'GoogleCalendarCLIAPI@1.5.0' or 'gmail').
- owner (OPTIONAL): The user ID to check authentications for. Defaults to 'me' (current user) if not provided. Pass a customuser_id to check authentications for a specific user if the user asks to use auths from a different user.`;

export const authenticationCheckAtom = atom<
  JotaiTool<z.infer<typeof CheckAuthParams>>
>(() => ({
  name: "authentication_check",
  description: authenticationCheckDescription,
  parameters: CheckAuthParams,
  execute: atom(null, async (get, set, { args }) => {
    return authenticationCheckExecute(args);
  }),
}));
authenticationCheckAtom.debugLabel = "authenticationCheckAtom";

/* eslint-enable sort-exports/sort-exports */
